import { motion } from "framer-motion";
import {
  Boxes, Terminal, Server, HardDrive, ShieldCheck, Router, Wifi,
  LockKeyhole, Cloud, Network, Container, Rocket, Database, GitBranch,
  Activity, Cctv,
} from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { spotlight } from "@/components/site/fx";
import { useI18n } from "@/i18n";

const TECH = [
  { name: "Proxmox", icon: Boxes, cat: "virt" },
  { name: "Linux", icon: Terminal, cat: "virt" },
  { name: "Windows Server", icon: Server, cat: "virt" },
  { name: "ZFS", icon: HardDrive, cat: "virt" },
  { name: "pfSense", icon: ShieldCheck, cat: "net" },
  { name: "MikroTik", icon: Router, cat: "net" },
  { name: "OpenWrt", icon: Wifi, cat: "net" },
  { name: "WireGuard", icon: LockKeyhole, cat: "net" },
  { name: "Cloudflare", icon: Cloud, cat: "net" },
  { name: "HAProxy", icon: Network, cat: "net" },
  { name: "Docker", icon: Container, cat: "cloud" },
  { name: "Coolify", icon: Rocket, cat: "cloud" },
  { name: "Nextcloud", icon: Cloud, cat: "cloud" },
  { name: "Supabase", icon: Database, cat: "cloud" },
  { name: "GitHub", icon: GitBranch, cat: "cloud" },
  { name: "Zabbix", icon: Activity, cat: "ops" },
  { name: "Dahua", icon: Cctv, cat: "ops" },
];

const CAT_COLOR = {
  virt: "#00E5FF",
  net: "#ff7a97",
  cloud: "#7C5CFC",
  ops: "#34d399",
};

const EASE = [0.22, 1, 0.36, 1];

export const TechStack = () => {
  const { t } = useI18n();
  return (
    <section
      id="stack"
      data-testid="techstack-section"
      className="relative border-t border-white/10 bg-ink-900"
    >
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ • ]">{t("tech.label")}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("tech.h1")} <span className="text-aurora">{t("tech.hkw")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">
              {t("tech.sub")}
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {TECH.map((t, i) => {
            const Icon = t.icon;
            const color = CAT_COLOR[t.cat];
            return (
              <motion.div
                key={t.name}
                {...spotlight()}
                data-testid={`tech-${t.name.toLowerCase().replace(/\s/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, ease: EASE, delay: (i % 6) * 0.05 }}
                whileHover={{ y: -5 }}
                className="spotlight card-edge group flex flex-col items-center justify-center gap-3 rounded-xl px-3 py-6 text-center"
              >
                <span
                  className="grid h-12 w-12 place-items-center rounded-lg border border-white/10 bg-ink-900/60 transition-colors duration-300"
                  style={{ color }}
                >
                  <Icon size={24} strokeWidth={1.6} />
                </span>
                <span className="font-display text-sm font-semibold text-white/90">{t.name}</span>
                <span className="h-1 w-6 rounded-full opacity-60 transition-opacity duration-300 group-hover:opacity-100" style={{ background: color }} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
